import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useOptionalCrmData } from '../../contexts/CrmDataContext';
import { usePortal } from '../../contexts/PortalContext';
import { emailToUsername, isValidEmail, normalizeEmail } from '../../lib/emailUtils';
import {
  addPortalAllowedEmail,
  allowlistWorkspaceLabel,
  fetchPortalAllowedEmailRemovals,
  fetchPortalAllowedEmails,
  removePortalAllowedEmail,
  updatePortalAllowedEmail,
  type AllowlistPortalKind,
  type PortalAllowedEmailRemoval,
} from '../../lib/portalAllowlist';
import { IS_UNIFIED_PORTAL, type PortalKind } from '../../lib/portal';
import {
  assignableAllowlistRoleOptions,
  assertAllowlistUpdateAllowed,
  canAddAllowlistWithRole,
  canDeleteAllowedEmail,
  canEditAllowedEmail,
  canManageAllowedEmails,
  deleteAllowedEmailHint,
  normalizeUserRole,
} from '../../lib/settingsPermissions';
import type { AllowedEmail, UserRole } from '../../types/crm';
import { AllowedEmailEditModal } from './AllowedEmailEditModal';
import { formatSingaporeDateTime } from '../../utils/dateTime';

function resolveAllowlistKind(
  allowlistKind: AllowlistPortalKind | undefined,
  portalKind: PortalKind,
): AllowlistPortalKind {
  if (allowlistKind) return allowlistKind;
  // Inventory has no dedicated allowlist DB; access is IT/management via CRM/IT lists.
  if (portalKind === 'inventory') return 'crm';
  if (
    portalKind === 'crm' ||
    portalKind === 'it' ||
    portalKind === 'dem' ||
    portalKind === 'forms'
  ) {
    return portalKind;
  }
  return 'crm';
}

function roleBadgeClass(role: UserRole): string {
  if (role === 'it') return 'settings-role-badge settings-role-badge--it';
  if (role === 'admin') return 'settings-role-badge settings-role-badge--admin';
  if (role === 'supervisor') return 'settings-role-badge settings-role-badge--supervisor';
  if (role === 'management') return 'settings-role-badge settings-role-badge--management';
  return 'settings-role-badge settings-role-badge--user';
}

function userInitial(row: AllowedEmail): string {
  const name = row.display_name?.trim() || row.email;
  return (name[0] ?? '?').toUpperCase();
}

type AllowedEmailsPanelProps = {
  allowlistKind?: AllowlistPortalKind;
  actorRole?: UserRole;
  ownerLabelFn?: (userId: string) => string;
  onRosterChanged?: () => void | Promise<void>;
};

export function AllowedEmailsPanel({
  allowlistKind,
  actorRole: actorRoleProp,
  ownerLabelFn,
  onRosterChanged,
}: AllowedEmailsPanelProps = {}) {
  const { profile, role: authRole } = useAuth();
  const portal = usePortal();
  const crmData = useOptionalCrmData();
  const realRole = actorRoleProp ?? authRole;
  const kind = resolveAllowlistKind(allowlistKind, portal.kind);
  const ownerLabel =
    ownerLabelFn ??
    crmData?.ownerLabel ??
    ((userId: string) => `${userId.slice(0, 8)}…`);
  const refreshRoster = onRosterChanged ?? crmData?.refresh ?? (async () => {});
  const canManage = canManageAllowedEmails(realRole);

  const [rows, setRows] = useState<AllowedEmail[]>([]);
  const [removals, setRemovals] = useState<PortalAllowedEmailRemoval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [saving, setSaving] = useState(false);
  const [editingRow, setEditingRow] = useState<AllowedEmail | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const addableRoles = useMemo(() => assignableAllowlistRoleOptions(realRole), [realRole]);
  const workspaceLabel = allowlistWorkspaceLabel(kind);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, removalLog] = await Promise.all([
        fetchPortalAllowedEmails(kind),
        fetchPortalAllowedEmailRemovals(kind),
      ]);
      setRows(list);
      setRemovals(removalLog);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!addableRoles.some((option) => option.value === role)) {
      setRole(addableRoles[0]?.value ?? 'management');
    }
  }, [addableRoles, role]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!canManage) return;

    const normalized = normalizeEmail(email);
    if (!isValidEmail(normalized)) {
      setError('Enter a valid email address.');
      return;
    }

    if (!canAddAllowlistWithRole(realRole, role)) {
      setError('You cannot add a user with this role.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await addPortalAllowedEmail(kind, {
        email: normalized,
        displayName: displayName.trim() || null,
        role,
      });
      setEmail('');
      setDisplayName('');
      setRole(addableRoles[0]?.value ?? 'user');
      await load();
      await refreshRoster();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveEdit(patch: { display_name: string | null; role: UserRole }) {
    if (!editingRow) return;
    setEditSaving(true);
    setError(null);
    try {
      assertAllowlistUpdateAllowed(realRole, normalizeUserRole(editingRow.role), patch);
      await updatePortalAllowedEmail(kind, editingRow.id, patch);
      setEditingRow(null);
      await load();
      await refreshRoster();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditSaving(false);
    }
  }

  async function handleRemove(row: AllowedEmail) {
    if (!canDeleteAllowedEmail(realRole, normalizeUserRole(row.role))) return;

    const reason = window.prompt(
      `Reason for removing ${row.email} from the allowlist (required):`,
    );
    if (reason === null) return;
    if (!reason.trim()) {
      setError('A reason is required when removing a user.');
      return;
    }

    setError(null);
    try {
      await removePortalAllowedEmail(kind, row.id, reason);
      await load();
      await refreshRoster();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (!canManage) {
    return null;
  }

  return (
    <>
      <div className="settings-columns">
        <section className="card settings-panel settings-panel--primary">
          <div className="settings-panel__head">
            <div>
              <h2 className="settings-panel__title">Allowed emails</h2>
              <p className="settings-panel__lead">
                Control who can sign in with Microsoft and appear as account owners in{' '}
                {workspaceLabel}.
                {IS_UNIFIED_PORTAL && kind !== 'dem' && (
                  <>
                    {' '}
                    Each workspace has its own allowlist; user and admin roles are
                    independent. IT and management can access both workspaces.
                  </>
                )}
                {kind === 'dem' && (
                  <>
                    {' '}
                    Controls Microsoft sign-in for the web portal only. Desktop app users are managed
                    separately under DEM configuration.
                  </>
                )}
              </p>
            </div>
            <span className="settings-panel__count">{rows.length} users</span>
          </div>

          <p className="settings-panel__hint">{deleteAllowedEmailHint(realRole)}</p>

          {error && <div className="alert alert-error">{error}</div>}

          <div className="settings-add-card">
            <h3 className="settings-add-card__title">Add user</h3>
            <form className="settings-add-form" onSubmit={(e) => void handleAdd(e)}>
              <label className="field">
                <span className="field-label">Work email</span>
                <input
                  className="field-input"
                  type="email"
                  placeholder="name@banteckhan.com.sg"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">Display name</span>
                <input
                  className="field-input"
                  placeholder="Display name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Role</span>
                <select
                  className="field-input"
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                >
                  {addableRoles.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="btn-primary settings-add-form__submit" disabled={saving}>
                {saving ? 'Adding…' : 'Add user'}
              </button>
            </form>
          </div>

          {loading ? (
            <div className="settings-loading">
              <div className="empty-state__spinner" />
              <p>Loading team…</p>
            </div>
          ) : rows.length === 0 ? (
            <p className="settings-empty">No users yet. Add at least one email so they can sign in.</p>
          ) : (
            <ul className="settings-user-list">
              {rows.map((row) => {
              const canEdit = canEditAllowedEmail(realRole, normalizeUserRole(row.role));
              const canRemove = canDeleteAllowedEmail(realRole, normalizeUserRole(row.role));
                const isSelf = Boolean(
                  profile?.email &&
                    normalizeEmail(profile.email) === normalizeEmail(row.email),
                );
                const label = row.display_name?.trim() || row.username || row.email;
                const handle = row.username ?? emailToUsername(row.email);

                return (
                  <li key={row.id} className="settings-user-card">
                    <div className="settings-user-card__avatar" aria-hidden>
                      {userInitial(row)}
                    </div>
                    <div className="settings-user-card__main">
                      <div className="settings-user-card__title-row">
                        <span className="settings-user-card__name">{label}</span>
                        <span className={roleBadgeClass(row.role)}>{row.role}</span>
                      </div>
                      <p className="settings-user-card__email">{row.email}</p>
                      <div className="settings-user-card__meta">
                        <span className="settings-user-card__handle">@{handle}</span>
                        <span className="settings-user-card__sep" aria-hidden>
                          ·
                        </span>
                        <span className="settings-user-card__id" title={row.id}>
                          ID {row.id.slice(0, 8)}…
                        </span>
                        <span className="settings-user-card__sep" aria-hidden>
                          ·
                        </span>
                        {row.auth_id ? (
                          <span className="settings-user-card__signed-in">Signed in</span>
                        ) : (
                          <span className="settings-user-card__pending">Not signed in yet</span>
                        )}
                      </div>
                    </div>
                    <div className="settings-user-card__actions">
                      {canEdit && (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => setEditingRow(row)}
                        >
                          Edit
                        </button>
                      )}
                      {canRemove ? (
                        <button
                          type="button"
                          className="btn-secondary btn-sm settings-user-card__remove"
                          disabled={isSelf}
                          title={isSelf ? 'You cannot remove your own allowlist entry' : undefined}
                          onClick={() => void handleRemove(row)}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="card settings-panel settings-panel--history">
          <h2 className="settings-panel__title">Removal history</h2>
          <p className="settings-panel__lead">Audit log of removed allowlist users.</p>

          {loading ? (
            <div className="settings-loading settings-loading--compact">
              <div className="empty-state__spinner" />
            </div>
          ) : removals.length === 0 ? (
            <p className="settings-history-empty">No removals recorded yet.</p>
          ) : (
            <ul className="settings-history-list">
              {removals.map((r) => (
                <li key={r.id} className="settings-history-item">
                  <div className="settings-history-item__head">
                    <span className="settings-history-item__email">{r.email}</span>
                    <span className={roleBadgeClass(r.role as UserRole)}>{r.role}</span>
                  </div>
                  <p className="settings-history-item__when">
                    {r.deleted_at ? formatSingaporeDateTime(r.deleted_at) : '—'}
                  </p>
                  <p className="settings-history-item__by">
                    Removed by {ownerLabel(r.deleted_by)}
                  </p>
                  {r.reason && <p className="settings-history-item__reason">{r.reason}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <AllowedEmailEditModal
        open={Boolean(editingRow)}
        row={editingRow}
        actorRole={realRole}
        saving={editSaving}
        onClose={() => setEditingRow(null)}
        onSave={(patch) => void handleSaveEdit(patch)}
      />
    </>
  );
}
